/**
 * Plural forms, chosen by the browser's own CLDR data.
 *
 * Russian needs three forms where English needs two — 1 изменение,
 * 2 изменения, 5 изменений — and Kazakh needs one. Hand-rolled modulo
 * arithmetic gets Russian right but has to be rewritten for every language
 * added, so `Intl.PluralRules` picks the category and the dictionary supplies
 * the words.
 */
import { FORMAT_LOCALE, type Locale } from "../store/localeStore";

/**
 * The forms one entry can take. `one`/`other` are required because every
 * supported language uses them; `few`/`many` exist for Russian and fall back
 * to `other` when a language does not distinguish them.
 */
export interface PluralForms {
  one: string;
  few?: string;
  many?: string;
  other: string;
}

/**
 * Identity, but it pins the property's type to `PluralForms` inside an
 * `as const` dictionary. Without it the literal strings would widen into a
 * shape that `kk`/`en` could only satisfy by repeating Russian's `few`/`many`.
 */
export const forms = (value: PluralForms): PluralForms => value;

/**
 * Pick the form for `n` and substitute it into `{n}`.
 *
 * Two different locales on purpose: the category comes from the language
 * (Russian's three-way split is a fact about Russian), while the number itself
 * is formatted with that language's *conventions* — see FORMAT_LOCALE for why
 * those are not always the same tag.
 */
export function plural(locale: Locale, n: number, variants: PluralForms): string {
  const category = new Intl.PluralRules(locale).select(n);
  const form = variants[category as keyof PluralForms] ?? variants.other;
  return form.replace("{n}", new Intl.NumberFormat(FORMAT_LOCALE[locale]).format(n));
}
